import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';
import { themed } from './themed';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: themed.interactive.primaryBtn,
        primary: themed.interactive.primaryBtn,
        secondary: cn(themed.interactive.secondaryBtn, themed.surface.border),
        ghost: themed.interactive.ghostBtn,
        destructive: themed.interactive.destructiveBtn,
        outline: cn(
          'border bg-transparent text-[var(--text-primary)] hover:bg-[var(--bg-surface-muted)]',
          themed.surface.border,
        ),
        link: 'text-brand-600 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-lg px-8',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

/** Themed shadcn-style button — use this instead of raw `<button>` in product UI. */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ className, variant, size, asChild = false, ...props }, ref) {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }), themed.focusRing)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
